import React from 'react';
import { Box, Card, CardContent, CircularProgress, Divider, Grid, TextField, Typography } from '@mui/material';
import type { AgentConfigurationCardProps } from '../../../../types/features/storeDetails/agentConfigurationCard';
import { useAgentConfigurationCardLogic } from '../../../../hooks/logic/useAgentConfigurationCardLogic';
import AgentConfigurationExtensions from './AgentConfigurationExtensions';
import AgentConfigurationGilbarcoPaths from './AgentConfigurationGilbarcoPaths';
import AgentConfigurationHeader from './AgentConfigurationHeader';
import AgentConfigurationLastSeenBanner from './AgentConfigurationLastSeenBanner';
import AgentConfigurationLogsSection from './AgentConfigurationLogsSection';
import AgentConfigurationPosTypeSelect from './AgentConfigurationPosTypeSelect';
import AgentConfigurationSaveSection from './AgentConfigurationSaveSection';
import AgentConfigurationScannerProxy from './AgentConfigurationScannerProxy';
import AgentConfigurationVerifoneFields from './AgentConfigurationVerifoneFields';
import { adminColors, adminTypography } from '../../../../styles/adminDesignTokens';
import {
  adminCardContentSx,
  adminCombineSx,
  adminDividerSx,
  adminFieldLabelSx,
  adminFormFieldSx,
  adminSurfaceSx,
} from '../../../../styles/adminComponentStyles';

const AgentConfigurationCard: React.FC<AgentConfigurationCardProps> = ({
  isAgentLoading,
  hasAgentDeviceId,
  agentDeviceId,
  changingDevice,
  onChangeDevice,
  onRetryVerifoneLogin,
  canRetryVerifoneLogin,
  retryingVerifoneLogin,
  verifoneLoginRetryNeedsSave,
  control,
  setValue,
  availableExtensions,
  checkboxIcon,
  checkboxCheckedIcon,
  showSyncGamesToPos,
  syncGamesToPosEnabled,
  syncGamesToPosUpdating,
  onSyncGamesToPosChange,
  posType,
  selectedPosType,
  posTypeOptions,
  onPosTypeChange,
  posEnabled,
  onPosEnabledChange,
  onFetchLatestLogs,
  onDownloadLogs,
  onSave,
  saving,
  isValid,
  lastFetchedLabel,
  showLastSeen,
  lastSeenAt,
  lastSeenLabel,
  heartbeatIntervalMinutes,
}) => {
  const { model, actions } = useAgentConfigurationCardLogic({
    control,
    setValue,
    posType,
    selectedPosType,
    posTypeOptions,
    onPosTypeChange,
    onFetchLatestLogs,
  });

  const saveDisabled = saving || (model.showPosAgentFields && !isValid);

  return (
    <Grid size={{ xs: 12 }}>
      <Card
        elevation={0}
        sx={adminSurfaceSx}
      >
        <CardContent sx={adminCardContentSx}>
          <Box sx={{ backgroundColor: '#F8FBFF', borderBottom: '1px solid #ECEEF3', mx: -2, mt: -2, px: 2, pt: 1, pb: 1, mb: 3 }}>
            <AgentConfigurationHeader
              isAgentLoading={isAgentLoading}
              hasAgentDeviceId={hasAgentDeviceId}
              agentDeviceId={agentDeviceId}
              changingDevice={changingDevice}
              onChangeDevice={onChangeDevice}
              posEnabled={posEnabled}
              onPosEnabledChange={onPosEnabledChange}
              checkboxIcon={checkboxIcon}
              checkboxCheckedIcon={checkboxCheckedIcon}
              showSyncGamesToPos={showSyncGamesToPos}
              syncGamesToPosEnabled={syncGamesToPosEnabled}
              syncGamesToPosUpdating={syncGamesToPosUpdating}
              onSyncGamesToPosChange={onSyncGamesToPosChange}
            />
          </Box>

          <AgentConfigurationPosTypeSelect
            selectedPosValue={model.selectedPosValue}
            posTypeMenuOpen={model.posTypeMenuOpen}
            posTypeOptions={posTypeOptions}
            posTypeLabel={model.posTypeLabel}
            hasSelectedOption={model.hasSelectedOption}
            onOpenMenu={() => actions.setPosTypeMenuOpen(true)}
            onCloseMenu={() => actions.setPosTypeMenuOpen(false)}
            onSelect={actions.handlePosTypeSelect}
          />

          {isAgentLoading ? (
            <Box sx={{ alignItems: 'center', display: 'flex', flexDirection: 'column', gap: 1.5, justifyContent: 'center', py: 6 }}>
              <Typography sx={{ ...adminTypography.sectionTitle, color: adminColors.text }}>
                Agent Configuration
              </Typography>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <>
              {model.showVerifoneFields ? (
                <AgentConfigurationVerifoneFields
                  control={control}
                  onRetryVerifoneLogin={onRetryVerifoneLogin}
                  canRetryVerifoneLogin={canRetryVerifoneLogin}
                  retryingVerifoneLogin={retryingVerifoneLogin}
                  verifoneLoginRetryNeedsSave={verifoneLoginRetryNeedsSave}
                />
              ) : null}

              {model.showPosAgentFields && model.showGilbarcoFields ? (
                <AgentConfigurationGilbarcoPaths
                  control={control}
                  readPathHasValue={model.readPathHasValue}
                  writePathHasValue={model.writePathHasValue}
                />
              ) : null}

              {model.showGilbarcoFields ? (
                <AgentConfigurationExtensions
                  availableExtensions={availableExtensions}
                  control={control}
                  checkboxIcon={checkboxIcon}
                  checkboxCheckedIcon={checkboxCheckedIcon}
                />
              ) : null}

              {model.showScannerProxyFields ? <AgentConfigurationScannerProxy control={control} /> : null}

              {model.showPosAgentFields ? (
                <Box sx={{ mb: 3 }}>
                  <Typography component="label" htmlFor="agent-heartbeat-interval" sx={{ ...adminFieldLabelSx, display: 'block', mb: 0.5 }}>
                    Heartbeat Interval (minutes)
                  </Typography>
                  <TextField
                    id="agent-heartbeat-interval"
                    name={model.heartbeatField.name}
                    inputRef={model.heartbeatField.ref}
                    value={model.heartbeatField.value ?? ''}
                    onChange={actions.handleHeartbeatIntervalChange}
                    onBlur={model.heartbeatField.onBlur}
                    error={Boolean(model.heartbeatError)}
                    helperText={model.heartbeatError}
                    placeholder="Enter interval in minutes"
                    slotProps={{ htmlInput: { inputMode: 'numeric', pattern: '[0-9]*' } }}
                    size="small"
                    fullWidth
                    sx={adminFormFieldSx}
                  />
                </Box>
              ) : null}

              <AgentConfigurationSaveSection onSave={onSave} saving={saving} disabled={saveDisabled} />

              {hasAgentDeviceId ? (
                <AgentConfigurationLogsSection
                  logsFetchState={model.logsFetchState}
                  onGetLatestLogs={() => void actions.handleGetLatestLogs()}
                  onDownloadLogs={onDownloadLogs}
                  lastFetchedLabel={lastFetchedLabel}
                />
              ) : null}
            </>
          )}
        </CardContent>

        {showLastSeen ? (
          <AgentConfigurationLastSeenBanner
            lastSeenAt={lastSeenAt}
            lastSeenLabel={lastSeenLabel}
            heartbeatIntervalMinutes={heartbeatIntervalMinutes}
          />
        ) : null}
      </Card>
    </Grid>
  );
};

export default React.memo(AgentConfigurationCard);
